<script setup lang="ts">
import { ref, computed } from 'vue';
import {
  equipArtifact, unequipSlot, repairArtifact, upgradeArtifact, fetchInventory,
  type Artifact, type EquipSlot, type Inventory,
} from '../game/inventory-api';
import { quickSellArtifact } from '../game/market-api';
import { itemArt } from '../game/art';
import { toast } from '../game/toast';
import { confirmAction } from '../game/confirm';
import cashIcon from '../assets/omerta/icons/cash.webp';
import {
  RARITY_HEX, SLOT_LABELS, STAT_META, UPGRADE_COST, MAX_ARTIFACT_LEVEL,
  scaledStat, scaledStats, compareStats, needsRepair, durabilityPct, repairCostPreview,
} from '../game/item-math';

/**
 * Self-contained item detail modal — performs its own gear mutations (equip /
 * unequip / upgrade / repair / quick-sell) and emits the fresh Inventory so any
 * parent (the Stash grid, the hero-card equipped strip, or the Dossier
 * paperdoll) can share ONE instance without owning the API calls itself.
 *
 * CATALOG MODE (`mode="catalog"`) — used by Market.vue's Rack to preview NPC
 * catalog / Vault gear you don't yet own. Hides all owned-only mutations
 * (equip/unequip/upgrade/repair/sell) and swaps in a single Buy action; the
 * caller supplies a synthetic Artifact (real id from the catalog/vault entry)
 * so the stat/compare-vs-equipped section above still works unchanged.
 */
const props = withDefaults(defineProps<{
  artifact: Artifact;
  slot: EquipSlot;
  isEquipped: boolean;
  /** Whatever currently occupies this slot (may equal `artifact` when isEquipped). */
  equippedInSlot: Artifact | null;
  mode?: 'owned' | 'catalog';
  /** Preformatted price string for the Buy action, e.g. "1,240 cash". */
  priceLabel?: string;
  /** Second price, when the piece is also sold for gold bars (always better value). */
  altPriceLabel?: string;
  buyBusy?: boolean;
  /** Disables + relabels the Buy button (e.g. "Not enough funds", "Stash full"). */
  blockedReason?: string | null;
}>(), { mode: 'owned', priceLabel: '', altPriceLabel: '', buyBusy: false, blockedReason: null });
const emit = defineEmits<{
  (e: 'updated', inv: Inventory): void;
  (e: 'buy'): void;
  (e: 'buy-alt'): void;
  (e: 'close'): void;
}>();

const busy = ref(false);
const error = ref('');

const rarHex = computed(() => RARITY_HEX[props.artifact.rarity]);
const stats = computed(() => scaledStats(props.artifact));
const compareRows = computed(() =>
  !props.isEquipped ? compareStats(props.artifact, props.equippedInSlot) : []);
const canUpgrade = computed(() => props.artifact.level < MAX_ARTIFACT_LEVEL);
const upgradeCost = computed(() => UPGRADE_COST[props.artifact.level] ?? 0);
const upgradePreview = computed(() => {
  const next = props.artifact.level + 1;
  const keys: ('attack' | 'defense' | 'magic' | 'speed' | 'hp')[] = ['attack', 'defense', 'magic', 'speed', 'hp'];
  return keys
    .map((k) => ({
      key: k,
      label: k === 'hp' ? 'Health' : STAT_META[k].label,
      before: scaledStat(props.artifact[k], props.artifact.level),
      after: scaledStat(props.artifact[k], next),
    }))
    .filter((r) => r.before !== 0 || r.after !== 0);
});
const repairCost = computed(() => repairCostPreview(props.artifact));
const showRepair = computed(() => needsRepair(props.artifact));

async function run(fn: () => Promise<Inventory>, done?: string) {
  busy.value = true; error.value = '';
  try {
    const inv = await fn();
    if (done) toast(done, { icon: itemArt(props.artifact.type, props.artifact.rarity, props.artifact.name), accent: rarHex.value });
    emit('updated', inv);
  } catch (e) { error.value = (e as Error).message; }
  finally { busy.value = false; }
}

function onEquip() { void run(() => equipArtifact(props.artifact.id), `Equipped ${props.artifact.name}`); }
function onUnequip() { void run(() => unequipSlot(props.slot), `Unequipped ${props.artifact.name}`); }
async function onRepair() {
  const ok = await confirmAction({
    kicker: 'The workshop',
    title: `Repair ${props.artifact.name}?`,
    icon: itemArt(props.artifact.type, props.artifact.rarity, props.artifact.name),
    rows: [{ label: 'Price', value: repairCost.value.toLocaleString(), icon: cashIcon }],
    confirmLabel: 'Repair',
  });
  if (!ok) return;
  void run(async () => (await repairArtifact(props.artifact.id)).inventory, `Repaired ${props.artifact.name}`);
}
async function onUpgrade() {
  const ok = await confirmAction({
    kicker: 'The workshop',
    title: `Upgrade ${props.artifact.name}?`,
    icon: itemArt(props.artifact.type, props.artifact.rarity, props.artifact.name),
    rows: [
      { label: 'Level', value: `${props.artifact.level} → ${props.artifact.level + 1}`, tone: 'dim' },
      { label: 'Price', value: upgradeCost.value.toLocaleString(), icon: cashIcon },
    ],
    confirmLabel: 'Upgrade',
  });
  if (!ok) return;
  void run(async () => (await upgradeArtifact(props.artifact.id)).inventory, `${props.artifact.name} upgraded to Lv ${props.artifact.level + 1}`);
}

/** Mirrors MarketService.quickSell: 50% of the drifting street value (fall back
 *  to base price). Preview only — the server's number is what actually pays. */
const sellPayout = computed(() => {
  const street = props.artifact.currentPrice > 0 ? props.artifact.currentPrice : (props.artifact.basePrice || 100);
  return Math.max(1, Math.round(street * 0.5));
});

async function onSell() {
  const ok = await confirmAction({
    kicker: 'Quick sell',
    title: `Sell ${props.artifact.name}?`,
    icon: itemArt(props.artifact.type, props.artifact.rarity, props.artifact.name),
    note: 'Instant payout, half street value — and sold is sold.',
    rows: [{ label: 'Payout', value: `${sellPayout.value.toLocaleString()} cash`, icon: cashIcon, tone: 'bright' }],
    confirmLabel: 'Sell it',
    danger: true,
  });
  if (!ok) return;
  busy.value = true; error.value = '';
  try {
    const res = await quickSellArtifact(props.artifact.id);
    toast(`Sold ${props.artifact.name} for ${res.value.toLocaleString()} cash`,
      { icon: itemArt(props.artifact.type, props.artifact.rarity, props.artifact.name), accent: rarHex.value });
    // the sold item is gone NOW — hand the fresh bag to the parent like every
    // other action does, or the stash keeps showing it until a page reload
    try { emit('updated', await fetchInventory()); } catch { /* parent refetches on next nav */ }
    emit('close');
  } catch (e) { error.value = (e as Error).message; }
  finally { busy.value = false; }
}
</script>

<template>
  <div class="fixed inset-0 z-[200] grid place-items-center p-4" style="background: rgba(6,4,12,0.85)" @click.self="emit('close')">
    <div class="im-frame relative w-full max-w-[480px] max-h-[88vh] overflow-y-auto rounded-xl border bg-surface"
         :style="{ borderColor: `color-mix(in srgb, ${rarHex} 55%, transparent)`, boxShadow: `0 10px 40px rgba(0,0,0,0.6), 0 0 24px color-mix(in srgb, ${rarHex} 25%, transparent)` }">
      <span class="corner-bracket corner-tl" :style="{ '--cb': rarHex }"></span>
      <span class="corner-bracket corner-tr" :style="{ '--cb': rarHex }"></span>
      <span class="corner-bracket corner-bl" :style="{ '--cb': rarHex }"></span>
      <span class="corner-bracket corner-br" :style="{ '--cb': rarHex }"></span>

      <button class="btn-deco btn-deco--ghost btn-deco-icon absolute top-3 right-3 z-10" aria-label="Close" @click="emit('close')">&times;</button>

      <!-- header -->
      <div class="relative p-5 pb-4 border-b border-border flex items-start gap-3.5">
        <img :src="itemArt(artifact.type, artifact.rarity, artifact.name)" :alt="artifact.type" class="w-16 h-16 rounded-lg object-cover shrink-0"
             :style="{ border: `1px solid color-mix(in srgb, ${rarHex} 55%, transparent)`, boxShadow: `0 2px 10px rgba(0,0,0,0.5)` }" />
        <div class="min-w-0 pt-0.5">
          <div class="flex items-center gap-2 flex-wrap">
            <h2 class="font-heading text-xl leading-none" :style="{ color: rarHex }">{{ artifact.name }}</h2>
            <span v-if="isEquipped" class="im-tag leading-none" style="--rar:#c8a24c">Equipped</span>
          </div>
          <div class="mt-1 flex items-center gap-2 font-label uppercase tracking-wider text-[11px] text-text-dim">
            <span>{{ SLOT_LABELS[slot] }}</span>
            <span class="opacity-50">·</span>
            <span :style="{ color: rarHex }">{{ artifact.rarity.toLowerCase() }}</span>
            <span class="opacity-50">·</span>
            <span>Lv {{ artifact.level }} / {{ MAX_ARTIFACT_LEVEL }}</span>
          </div>
          <p v-if="artifact.description" class="font-body text-text-dim text-xs mt-1.5 leading-snug">{{ artifact.description }}</p>
          <div v-if="artifact.setName" class="mt-1.5 font-body text-xs text-gold capitalize">{{ artifact.setName }} set</div>
          <div v-if="artifact.statusEffect" class="mt-0.5 font-body text-xs text-purple-light">{{ artifact.statusEffect }}</div>
        </div>
      </div>

      <div class="p-5 pt-4">
        <p v-if="error" class="mb-3 text-sm text-error">{{ error }}</p>

        <!-- stats (with compare-vs-equipped deltas) -->
        <div class="im-h mb-2">{{ isEquipped ? 'Contributing' : compareRows.length ? 'Vs. equipped' : 'Stats' }}</div>
        <div class="flex flex-col gap-1.5 mb-4">
          <template v-if="!isEquipped && compareRows.length">
            <div v-for="r in compareRows" :key="r.key" class="flex items-center justify-between gap-2">
              <span class="font-label uppercase tracking-wider text-[11px] text-text-dim w-20 shrink-0 leading-none">{{ r.label }}</span>
              <span class="font-body tabular-nums text-text flex-1 text-right leading-none">{{ r.candidate }}</span>
              <span class="font-body tabular-nums text-xs w-14 text-right leading-none"
                    :class="r.delta > 0 ? 'text-success' : r.delta < 0 ? 'text-error' : 'text-text-dim'">
                {{ r.delta > 0 ? '+' : '' }}{{ r.delta }}
              </span>
            </div>
          </template>
          <template v-else>
            <div v-for="(v, k) in stats" :key="k" v-show="v !== 0" class="flex items-center justify-between gap-2">
              <span class="font-label uppercase tracking-wider text-[11px] text-text-dim leading-none">{{ k === 'hp' ? 'Health' : STAT_META[k as 'attack'].label }}</span>
              <span class="font-body tabular-nums text-gold leading-none">+{{ v }}</span>
            </div>
          </template>
        </div>

        <!-- durability (owned items only) -->
        <template v-if="mode === 'owned' && artifact.isBreakable && artifact.maxDurability != null">
          <div class="im-h mb-1.5">Durability</div>
          <div class="wear-track mb-3">
            <div class="wear-fill" :style="{ width: durabilityPct(artifact) + '%', background: durabilityPct(artifact) > 50 ? 'var(--color-success)' : durabilityPct(artifact) > 25 ? '#d9a441' : 'var(--color-error)' }"></div>
          </div>
        </template>

        <!-- upgrade preview (owned items only) -->
        <template v-if="mode === 'owned'">
          <template v-if="canUpgrade">
            <div class="im-h mb-1.5">Upgrade — Lv {{ artifact.level }} → {{ artifact.level + 1 }}</div>
            <div class="flex flex-col gap-1 mb-3 rounded-md border border-gold/25 bg-gold/5 p-2.5">
              <div v-for="r in upgradePreview" :key="r.key" class="flex items-center justify-between text-xs">
                <span class="font-label uppercase tracking-wider text-text-dim leading-none">{{ r.label }}</span>
                <span class="font-body tabular-nums text-text leading-none">{{ r.before }} <span class="text-gold">→ {{ r.after }}</span></span>
              </div>
            </div>
          </template>
          <p v-else class="font-body text-xs text-text-dim mb-3 italic">Max level reached.</p>
        </template>

        <!-- actions: owned-item mutations -->
        <div v-if="mode === 'owned'" class="flex flex-wrap gap-2 mt-4">
          <button v-if="!isEquipped" class="btn-deco btn-deco--gold btn-deco--sm" :disabled="busy" @click="onEquip">{{ busy ? '…' : 'Equip' }}</button>
          <button v-else class="btn-deco btn-deco--ghost btn-deco--sm" :disabled="busy" @click="onUnequip">{{ busy ? '…' : 'Unequip' }}</button>

          <button v-if="canUpgrade" class="btn-deco btn-deco--sm" :disabled="busy" @click="onUpgrade">
            {{ busy ? '…' : `Upgrade · ${upgradeCost} cash` }}
          </button>

          <button v-if="showRepair" class="btn-deco btn-deco--sm" :disabled="busy" @click="onRepair">
            {{ busy ? '…' : `Repair · ${repairCost} cash` }}
          </button>

          <button v-if="!isEquipped" class="btn-deco btn-deco--blood btn-deco--sm ml-auto" :disabled="busy" @click="onSell">
            {{ busy ? '…' : 'Sell' }}
          </button>
        </div>

        <!-- actions: catalog / vault preview — single Buy action -->
        <div v-else class="flex mt-4">
          <button
            class="btn-deco btn-deco--gold w-full"
            :disabled="buyBusy || !!blockedReason"
            @click="emit('buy')"
          >{{ buyBusy ? '…' : (blockedReason ?? `Buy · ${priceLabel}`) }}</button>
          <!-- Gold bars are always the better value (dominion's model), so the
               premium price sits alongside the cash one rather than replacing it. -->
          <button
            v-if="altPriceLabel"
            class="btn-deco btn-deco--gold w-full mt-2"
            :disabled="buyBusy || !!blockedReason"
            @click="emit('buy-alt')"
          >{{ buyBusy ? '…' : `Buy · ${altPriceLabel}` }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.im-h {
  font-family: var(--font-label, sans-serif);
  text-transform: uppercase; letter-spacing: 0.16em;
  font-size: 11px; color: var(--color-text-dim, #8a8378);
}
.im-tag {
  --rar: #9a927f;
  font-family: var(--font-label, sans-serif);
  font-size: 9px; text-transform: uppercase; letter-spacing: 0.1em;
  padding: 2px 7px; border-radius: 999px;
  color: var(--rar);
  border: 1px solid color-mix(in srgb, var(--rar) 55%, transparent);
  background: color-mix(in srgb, var(--rar) 15%, transparent);
}
.wear-track { height: 6px; border-radius: 999px; background: rgba(0,0,0,0.5); border: 1px solid rgba(0,0,0,0.4); overflow: hidden; }
.wear-fill { height: 100%; border-radius: 999px; transition: width 400ms ease-out; }
</style>
